// ----------------------------------------------------------------------
// A heading, in words.
//
// Tesla reports `drive_state.heading` as degrees clockwise from true north,
// 0 to 360, and the map turns the car by exactly that. A rotation is the right
// drawing and the wrong thing to *say*: nobody reads "facing 47 degrees", and
// somebody who cannot see the marker turn gets nothing at all from it.
//
// So the number is drawn and the compass point is spoken. Sixteen points is
// the wrong granularity for a sentence and four is a lie at 44 degrees, so it
// is the eight an operator in a car park actually uses.
// ----------------------------------------------------------------------

/** The eight points, clockwise from north, as the sentence says them. */
export const COMPASS_POINTS = [
  'north',
  'north-east',
  'east',
  'south-east',
  'south',
  'south-west',
  'west',
  'north-west',
] as const

export type CompassPoint = (typeof COMPASS_POINTS)[number]

const SECTOR = 360 / COMPASS_POINTS.length

/**
 * The compass point a bearing falls in, or `null` when there is no bearing.
 *
 * Each point owns the 45 degrees centred on it, so north is 337.5 to 22.5 and
 * not 0 to 45: a car at 30 degrees is north-east, and rounding it to north
 * would point a person at the wrong end of a row.
 *
 * `null` rather than north for an absent reading. The schema types this field
 * non-null, so an absent heading arrives as `0`, which is a real bearing and
 * means due north: a caller that knows it has no reading has to say so, and
 * one that has 0 gets north because that is what 0 is.
 */
export const bearing = (
  degrees: number | null | undefined,
): CompassPoint | null => {
  if (degrees === null || degrees === undefined || !Number.isFinite(degrees)) {
    return null
  }
  // `%` keeps the sign of its left operand in JavaScript, so a negative
  // bearing needs the second modulo or it indexes off the front of the table.
  const normalised = ((degrees % 360) + 360) % 360
  return COMPASS_POINTS[Math.round(normalised / SECTOR) % COMPASS_POINTS.length]
}
